import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { SectiuniPubliceService } from '../sectiuni-publice.service';
import { NotificationService } from 'src/app/services/notification.service';
import { AuthService } from 'src/app/services/auth.service';
import { SectiuniPublice } from '../sectiuni-publice.interface';
import { Observable, map, finalize, Subject, of, switchMap, distinctUntilChanged, takeUntil, tap, catchError } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CompartimenteService } from '../../../../settings-sidenav/compartimente/compartimente.service';
import { notDefaultValidator } from 'src/app/shared/validators/not-default';

@Component({
    selector: 'app-add-sectiuni-publice',
    templateUrl: './add-sectiuni-publice.component.html',
    styleUrls: ['./add-sectiuni-publice.component.scss']
})
export class AddSectiuniPubliceComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;
    form: FormGroup = new FormGroup({
        id_directie: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        id_compartiment: new FormControl(-1),
        id_group: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
        content: new FormControl('', [Validators.required]),
        observations: new FormControl(''),
    });

    directiiActive: Generic[] = this.data.directiiActive;
    compartimenteActive!: Generic[];
    categoriiDocumente: Generic[] = this.data.categoriiDocumente;

    private addDestroy$ = new Subject<void>();

    filteredDirectii: any[] = this.data.directiiActive;
    directiiControl = new FormControl();

    filteredCompartimente: any[] = [];
    compartimenteControl = new FormControl();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<AddSectiuniPubliceComponent>,
        private sectiuniPubliceService: SectiuniPubliceService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private compartimenteService: CompartimenteService
    ) {
        this.processToken();
    }

    ngOnInit() {
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.sectiuniPubliceService.setToken(token);
        });
    }

    renderFieldsSearch() {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredDirectii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    submitForm() {
        this.isLoading = true;

        const { id_directie, id_compartiment, id_group, content, observations } = this.form
            .value as SectiuniPublice;
        const newSectiune: SectiuniPublice = {
            id_directie,
            id_compartiment,
            id_group,
            content,
            observations,
        };

        this.sectiuniPubliceService
            .addSectiune(newSectiune)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directiiActive.filter(directie =>
            this.normalizeString(directie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onDirectieSelectClosed() {
        const selectedValue = this.directiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredDirectii.some(directie =>
                directie.id === this.form.get('id_directie')?.value
            );

            if (!isSelectedInFilteredList) {
                this.directiiControl.setValue(null);
                this.filteredDirectii = this.directiiActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['id_compartiment'].patchValue(-1);

        if (directieId == -1) {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            return;
        }

        if (directieId) {
            this.loadCompartimenteActive(directieId);
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.compartimenteService.getCompartimenteByDirectieId(directieId)
            : this.compartimenteService.getCompartimenteActive();

        compartimenteObservable$.pipe(
            map((results: any) => [...results]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (res) => {
                this.compartimenteActive = [...res];
                this.filteredCompartimente = [...res];
            },
            error: (err) => {
                console.log(err);
                this.filteredCompartimente = [];
            },
        });
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.compartimenteActive.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(compartiment =>
                compartiment.id === this.form.get('id_compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.compartimenteActive;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }
}
